import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { TrendingUp, TrendingDown, Minus, AlertTriangle } from 'lucide-react';
import { predictions2025, cropYieldData, weatherForecast2025, provinces, crops } from '../data/mockData';

const PredictionPanel = () => {
  const [selectedCrop, setSelectedCrop] = useState('Wheat');
  
  const getPredictionTrend = (crop, province) => {
    const currentYear = cropYieldData[crop][province].find(d => d.year === 2024);
    const prediction = predictions2025[crop][province];
    
    if (!currentYear || !prediction) return { trend: 'stable', change: 0 };
    
    const change = ((prediction.yield - currentYear.yield) / currentYear.yield) * 100;
    
    if (change > 2) return { trend: 'up', change: change.toFixed(1) };
    if (change < -2) return { trend: 'down', change: change.toFixed(1) };
    return { trend: 'stable', change: change.toFixed(1) };
  };

  const getTrendIcon = (trend) => {
    switch(trend) {
      case 'up': return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'down': return <TrendingDown className="h-4 w-4 text-red-500" />;
      default: return <Minus className="h-4 w-4 text-gray-500" />;
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.85) return 'bg-green-100 text-green-800';
    if (confidence >= 0.80) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getWeatherRisk = (province) => {
    const forecast = weatherForecast2025[province];
    const extremeEvents = forecast.extremeHeat + forecast.extremeCold;
    
    if (extremeEvents <= 4) return { level: 'Low', color: 'bg-green-100 text-green-800' };
    if (extremeEvents <= 7) return { level: 'Medium', color: 'bg-yellow-100 text-yellow-800' };
    return { level: 'High', color: 'bg-red-100 text-red-800' };
  };

  const calculateNationalTotal = (crop) => {
    let totalProduction = 0;
    let totalArea = 0;
    
    provinces.forEach(province => {
      const prediction = predictions2025[crop][province.id];
      if (prediction) {
        totalProduction += prediction.production;
        totalArea += prediction.area;
      }
    });
    
    return {
      production: totalProduction,
      area: totalArea,
      yield: totalProduction / totalArea
    };
  };

  const nationalTotal = calculateNationalTotal(selectedCrop);

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>2025 Crop Predictions</span>
            <Select value={selectedCrop} onValueChange={setSelectedCrop}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {crops.map(crop => (
                  <SelectItem key={crop} value={crop}>
                    {crop}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardTitle>
        </CardHeader>
      </Card>

      {/* National Summary */}
      <Card>
        <CardHeader>
          <CardTitle>National {selectedCrop} Forecast</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {nationalTotal.yield.toFixed(1)}
              </div>
              <div className="text-sm text-gray-600">Avg Yield (t/ha)</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {(nationalTotal.area / 1000000).toFixed(1)}M
              </div>
              <div className="text-sm text-gray-600">Total Area (ha)</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {(nationalTotal.production / 1000000).toFixed(1)}M
              </div>
              <div className="text-sm text-gray-600">Total Production (t)</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Provincial Predictions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {provinces.map(province => {
          const prediction = predictions2025[selectedCrop][province.id];
          const trend = getPredictionTrend(selectedCrop, province.id);
          const weatherRisk = getWeatherRisk(province.id);
          
          return (
            <Card key={province.id}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{province.name}</span>
                  <div className="flex items-center gap-2">
                    {getTrendIcon(trend.trend)}
                    <span className={`text-sm font-medium ${
                      trend.trend === 'up' ? 'text-green-600' : 
                      trend.trend === 'down' ? 'text-red-600' : 'text-gray-600'
                    }`}>
                      {trend.change > 0 ? '+' : ''}{trend.change}%
                    </span>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-lg font-semibold text-blue-600">
                      {prediction.yield.toFixed(1)} t/ha
                    </div>
                    <div className="text-sm text-gray-600">Predicted Yield</div>
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-green-600">
                      {(prediction.production / 1000000).toFixed(1)}M t
                    </div>
                    <div className="text-sm text-gray-600">Production</div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Model Confidence:</span>
                    <Badge className={getConfidenceColor(prediction.confidence)}>
                      {(prediction.confidence * 100).toFixed(0)}%
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-gray-500" />
                    <Badge className={weatherRisk.color}>
                      {weatherRisk.level} Risk
                    </Badge>
                  </div>
                </div>
                
                <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
                  <div>Area: {(prediction.area / 1000).toFixed(0)}k ha</div>
                  <div>Weather Risk: {weatherRisk.level} due to forecast conditions</div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default PredictionPanel;